import {Box, Combobox, Input, useCombobox, useListCollection} from "@chakra-ui/react";
import {forwardRef, useEffect, useId, useMemo, useRef, useState} from "react";
import T from "platformed-components/i18n/T";
import useTranslate from "platformed-components/i18n/useTranslate";
import {Portal} from "../Portal";
import {ScalarValueInputProps} from "./types";

/**
 * Plain text input — used when no predefined options are provided.
 * Calls onChange on every keystroke; empty string is normalised to null.
 */
const PlainStringInput = forwardRef<HTMLInputElement, ScalarValueInputProps<string>>(
  ({value, onChange, placeholder, disabled = false}, ref) => {
    const t = useTranslate();
    const effectivePlaceholder = placeholder ?? t("value_input.type_to_search_placeholder");

    return (
      <Input
        ref={ref}
        size="sm"
        value={typeof value === "string" ? value : ""}
        placeholder={effectivePlaceholder}
        disabled={disabled}
        onChange={e => onChange(e.target.value || null)}
      />
    );
  },
);
PlainStringInput.displayName = "PlainStringInput";

/**
 * Combobox string input — used when predefined options are provided.
 * Supports selecting from a dropdown or typing a custom value.
 * Commits value on blur or Enter.
 */
const ComboboxStringInput = forwardRef<HTMLInputElement, ScalarValueInputProps<string>>(
  ({value, onChange, options = [], placeholder, disabled = false}, ref) => {
    const t = useTranslate();
    const effectivePlaceholder = placeholder ?? t("value_input.type_to_search_placeholder");
    const currentValue = typeof value === "string" ? value : "";

    // Local input state for typing
    const [localInputValue, setLocalInputValue] = useState(currentValue);
    const isEditingRef = useRef(false);

    // Sync local input when external value changes (unless user is actively editing)
    useEffect(() => {
      if (!isEditingRef.current) {
        setLocalInputValue(currentValue);
      }
    }, [currentValue]);

    type CollectionItem = {label: string; value: string};
    const collectionItems = useMemo<CollectionItem[]>(() => options.map(opt => ({label: opt, value: opt})), [options]);
    const {collection} = useListCollection({initialItems: collectionItems});

    const uid = useId();

    const combobox = useCombobox({
      id: uid,
      collection,
      inputValue: localInputValue,
      onInputValueChange: e => {
        isEditingRef.current = true;
        setLocalInputValue(e.inputValue);
      },
      allowCustomValue: true,
      openOnClick: options.length > 0,
      onValueChange: e => {
        const selected = e.value[0] ?? "";
        isEditingRef.current = false;
        setLocalInputValue(selected);
        onChange(selected || null);
      },
      disabled,
    });

    const commitValue = () => {
      const inputValue = localInputValue.trim();
      isEditingRef.current = false;
      if (inputValue !== currentValue) {
        onChange(inputValue || null);
      }
    };

    const handleBlur = () => {
      commitValue();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        commitValue();
      }
    };

    // Show dropdown hint only when there are options
    const showDropdownHint = options.length > 0;

    return (
      <Combobox.RootProvider value={combobox}>
        <Combobox.Control>
          <Combobox.Input
            ref={ref}
            placeholder={effectivePlaceholder}
            disabled={disabled}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />
          {showDropdownHint && (
            <Combobox.IndicatorGroup>
              <Combobox.Trigger />
            </Combobox.IndicatorGroup>
          )}
        </Combobox.Control>
        {showDropdownHint && (
          <Portal>
            <Combobox.Positioner>
              <Combobox.Content>
                {collection.items.map(item => (
                  <Combobox.Item item={item} key={item.value}>
                    {item.label}
                    <Combobox.ItemIndicator />
                  </Combobox.Item>
                ))}
                <Box px={2} py={1.5} fontSize="xs" color="fg.muted">
                  <T keyName="value_input.custom_value_hint" />
                </Box>
              </Combobox.Content>
            </Combobox.Positioner>
          </Portal>
        )}
      </Combobox.RootProvider>
    );
  },
);
ComboboxStringInput.displayName = "ComboboxStringInput";

/**
 * String input component
 *
 * Renders a plain text input when no options are provided,
 * or a combobox with dropdown when options exist.
 */
const StringInput = forwardRef<HTMLInputElement, ScalarValueInputProps<string>>((props, ref) => {
  const {options = []} = props;
  if (options.length === 0) {
    return <PlainStringInput ref={ref} {...props} />;
  }
  return <ComboboxStringInput ref={ref} {...props} />;
});
StringInput.displayName = "StringInput";

export default StringInput;
